import api from './client';
import { getAccessToken } from './client';

export interface LandingPromptPhrasesRequest {
  utm_term: string;
  landing_key: string;
  page_title: string;
  page_subtitle?: string;
  page_cta_hint?: string;
}

export interface LandingPromptPhrasesResponse {
  success: boolean;
  phrases: string[];
  cached?: boolean;
  error?: string;
}

type LandingPromptStreamDone = {
  success: boolean;
  phrases: string[];
  cached?: boolean;
};

function getCSRFToken(): string | null {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return cookieValue || null;
    }
  }
  return null;
}

export async function getLandingPromptPhrases(
  payload: LandingPromptPhrasesRequest,
): Promise<LandingPromptPhrasesResponse> {
  const response = await api.post('/landing/prompt-phrases/', payload, { timeout: 15000 });
  return response.data;
}

export function streamLandingPromptPhrases(
  payload: LandingPromptPhrasesRequest,
  callbacks: {
    onPhrase: (phrase: string, phrases: string[]) => void;
    onDone: (result: LandingPromptStreamDone) => void;
    onError: (error: string) => void;
  },
): AbortController {
  const controller = new AbortController();
  const accessToken = getAccessToken();
  const csrfToken = getCSRFToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }

  void (async () => {
    const phrases: string[] = [];
    try {
      const response = await fetch('/api/landing/prompt-phrases/stream/', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal: controller.signal,
        credentials: 'include',
      });

      if (!response.ok) {
        callbacks.onError(`HTTP ${response.status}: ${response.statusText}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('Streaming non disponibile');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      const processEvent = (part: string) => {
        if (!part.trim()) return;

        const lines = part.split('\n');
        let eventType = '';
        let eventData = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData = line.slice(6);
        }

        if (!eventData) return;

        try {
          const parsed = JSON.parse(eventData);
          if (eventType === 'phrase') {
            const phrase = String(parsed.phrase || '').trim();
            if (!phrase || phrases.includes(phrase)) return;
            phrases.push(phrase);
            callbacks.onPhrase(phrase, [...phrases]);
          } else if (eventType === 'done') {
            callbacks.onDone({
              success: Boolean(parsed.success),
              phrases: Array.isArray(parsed.phrases) ? parsed.phrases : [...phrases],
              cached: parsed.cached,
            });
          } else if (eventType === 'error') {
            callbacks.onError(parsed.error || 'Streaming error');
          }
        } catch {
          // Ignore malformed SSE payloads.
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          processEvent(part);
        }
      }

      if (buffer.trim()) {
        processEvent(buffer);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      callbacks.onError(err instanceof Error ? err.message : 'Stream connection failed');
    }
  })();

  return controller;
}
